import { useQueryClient } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import { useBacklinks, useUnlinked, useNote, qk } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { Api } from '../../lib/api';
import { TypeDot } from '../ui/TypeDot';

export function BacklinksPanel({ noteId }: { noteId: string | null }) {
  const note = useNote(noteId);
  const backlinks = useBacklinks(noteId);
  const unlinked = useUnlinked(noteId);
  const openNote = useUi((s) => s.openNote);
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();

  if (!noteId) return <div className="dock-empty">Open a note to see what links here.</div>;

  /** One-click "link it": wrap the first plain occurrence of the title in [[ ]] in the mentioning note. */
  const linkIt = async (mentionId: string) => {
    const title = note.data?.title;
    if (!title) return;
    try {
      const m = await Api.notes.get(mentionId);
      const re = new RegExp(`(?<![\\[\\w])${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\]\\w])`, 'i');
      const idx = m.raw.search(re);
      if (idx < 0) throw new Error('no plain occurrence found');
      const raw = m.raw.slice(0, idx) + `[[${title}]]` + m.raw.slice(idx + title.length);
      await Api.notes.update(mentionId, raw, m.contentHash);
      useUi.getState().dropDraft(mentionId);
      qc.invalidateQueries({ queryKey: qk.note(mentionId) });
      qc.invalidateQueries({ queryKey: qk.backlinks(noteId) });
      qc.invalidateQueries({ queryKey: qk.unlinked(noteId) });
      toast(`Linked from ${m.title}`);
    } catch (err) {
      toast(`Could not link: ${(err as Error).message}`, 'crit');
    }
  };

  return (
    <div>
      <div className="label">
        Linked mentions <span className="count">{backlinks.data?.length ?? ''}</span>
      </div>
      {backlinks.data?.length === 0 && <div className="dock-empty">Nothing links here yet.</div>}
      {backlinks.data?.map((b) => (
        <div key={b.edgeId} className="bl-item" onClick={(e) => openNote(b.fromId, { split: e.ctrlKey || e.metaKey })} title={b.fromPath}>
          <div className="t">
            <TypeDot type={b.fromType} />
            <span>{b.fromTitle}</span>
            <span className="chip" style={{ marginLeft: 'auto', fontSize: 10 }}>{b.kind === 'TYPED' ? b.context : b.kind.toLowerCase()}</span>
          </div>
          {b.kind !== 'TYPED' && b.context && <div className="ctx">{b.context}</div>}
        </div>
      ))}

      <div className="label" style={{ marginTop: 16 }}>
        Unlinked mentions <span className="count">{unlinked.data?.length ?? ''}</span>
      </div>
      {unlinked.data?.length === 0 && <div className="dock-empty">No unlinked mentions.</div>}
      {unlinked.data?.map((u) => (
        <div key={u.noteId} className="bl-item" onClick={(e) => openNote(u.noteId, { split: e.ctrlKey || e.metaKey })} title={u.path}>
          <div className="t">
            <span>{u.title}</span>
            <button className="ghost link-it" onClick={(e) => (e.stopPropagation(), void linkIt(u.noteId))} title="Turn the mention into a wikilink">
              <Link2 size={10} /> link it
            </button>
          </div>
          <div className="ctx" dangerouslySetInnerHTML={{ __html: sanitizeSnippet(u.snippet) }} />
        </div>
      ))}
    </div>
  );
}

/** Snippets come from FTS with <mark> only; strip anything else defensively. */
export function sanitizeSnippet(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/&lt;mark&gt;/g, '<mark>')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
}
